/* okkids shared wishlist -- browser-persisted (localStorage), used by the
   collection grid heart icons, the product page wishlist button, the header
   icon/count, and the wishlist page itself, so they all stay in sync.

   Products are keyed by handle (reliable everywhere -- every wishlist
   trigger already carries data-product-handle or a data-product-url we can
   derive a handle from). Fires `okk:wishlist:change` on <document> with the
   current list whenever it changes, so any UI piece can react without
   polling.

   Click handling for ANY element with [data-okk-wishlist-toggle] or class
   .okk-wishlist-btn is handled globally here, in one place, so individual
   sections (product page, collection grid) don't need their own duplicate
   toggle logic -- they just render the button with the right data
   attributes and this file takes care of the rest. Also keeps the header's
   count bubble ([data-okk-wishlist-count]) in sync.

   IMPORTANT: heart clicks live inside product cards, and Dawn (or other
   scripts) can have their own click handlers on the card that navigate to
   the product -- preventDefault() alone only cancels a native <a> link's
   own navigation, it does NOT stop *other* JS click listeners from also
   firing and navigating manually. stopImmediatePropagation() is used here
   so no other handler gets a chance to process this same click at all. */
window.okkWishlist = (function () {
  var KEY = 'okk_wishlist_v1';

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('okk:wishlist:change', { detail: { list: list } }));
  }

  function handleFromUrl(url) {
    if (!url) return '';
    var match = url.split('?')[0].match(/\/products\/([^/]+)/);
    return match ? match[1] : '';
  }

  function has(handle) {
    if (!handle) return false;
    return read().some(function (item) { return item.handle === handle; });
  }

  // item: { handle, url, title, image }. Returns true if just added, false if just removed.
  function toggle(item) {
    if (!item || !item.handle) return false;
    var list = read();
    var idx = list.findIndex(function (existing) { return existing.handle === item.handle; });
    var added;
    if (idx > -1) {
      list.splice(idx, 1);
      added = false;
    } else {
      list.push(item);
      added = true;
    }
    write(list);
    return added;
  }

  function remove(handle) {
    write(read().filter(function (item) { return item.handle !== handle; }));
  }

  function count() {
    return read().length;
  }

  function readTrigger(el) {
    var handle = el.getAttribute('data-product-handle') || handleFromUrl(el.getAttribute('data-product-url'));
    return {
      handle: handle,
      url: el.getAttribute('data-product-url') || '',
      title: el.getAttribute('data-product-title') || '',
      image: el.getAttribute('data-product-image') || ''
    };
  }

  function updateButton(el) {
    var handle = el.getAttribute('data-product-handle') || handleFromUrl(el.getAttribute('data-product-url'));
    el.classList.toggle('is-active', has(handle));
  }

  function updateCountBubbles() {
    var n = count();
    document.querySelectorAll('[data-okk-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  function updateAllButtons() {
    document.querySelectorAll('[data-okk-wishlist-toggle], .okk-wishlist-btn').forEach(updateButton);
    updateCountBubbles();
  }

  // Capture phase (true) so this runs BEFORE any bubble-phase handler a
  // product card might have for "click anywhere to navigate" -- and
  // stopImmediatePropagation() ensures nothing else sees this click at all
  // once we've claimed it.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-okk-wishlist-toggle], .okk-wishlist-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    var item = readTrigger(btn);
    if (!item.handle) return;
    toggle(item);
    updateAllButtons();
  }, true);

  document.addEventListener('okk:wishlist:change', updateAllButtons);
  document.addEventListener('DOMContentLoaded', updateAllButtons);
  // Cover sections that render after DOMContentLoaded (AJAX swaps, etc).
  document.addEventListener('shopify:section:load', updateAllButtons);
  if (document.readyState !== 'loading') updateAllButtons();

  return {
    read: read,
    has: has,
    toggle: toggle,
    remove: remove,
    count: count,
    updateAllButtons: updateAllButtons,
    handleFromUrl: handleFromUrl
  };
})();
